import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AskPayGBotHelperButton from '@/components/AskPayGBotHelperButton';
import useFeatureFlags from '@/hooks/useFeatureFlags';
import { motion } from 'framer-motion';
import { 
  Lightning, CheckCircle, Clock, ArrowRight, 
  Chat, Rocket, VideoCamera, PenNib, Calendar,
  Copy, Fire, Users, Heart, UserPlus, ChatCircleDots
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import api, { contentAPI } from '@/utils/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import ExecutionPanel from '@/components/ExecutionPanel';

const DailyMissionCard = ({ onStartMission, onCreateVideos, onPlanWeek }) => {
  const navigate = useNavigate();
  const [todayData, setTodayData] = useState(null);
  const [completedTasks, setCompletedTasks] = useState({});
  const [loading, setLoading] = useState(true);
  const [streakData, setStreakData] = useState(null);
  // 2026-05-29 Phase 2 — backend-driven CTA routing from
  // GET /api/content/today-mission. When `cta_label` AND `cta_route`
  // are both set, we replace the default Run-My-Week / Generate
  // primary button with a route-specific CTA. Otherwise behavior is
  // unchanged.
  const [missionCta, setMissionCta] = useState(null);
  const flags = useFeatureFlags();
  // 2026-06-10 Phase 2 — asset-driven Today's Mission. When v2
  // returns a real saved asset, the first content task surfaces
  // execution language ("Post Content #N — …") instead of the
  // generic "Create today's content".
  // 2026-02-13 Round 57 — v2 also returns the FULL hydrated asset
  // payload + tier + today_progress so Today's Mission can render
  // the asset inline as the primary execution surface.
  const [v2Mission, setV2Mission] = useState(null);
  const [v2AssetPayload, setV2AssetPayload] = useState(null);
  const [v2Tier, setV2Tier] = useState('starter');
  const [v2TierNote, setV2TierNote] = useState('');
  const [v2TodayProgress, setV2TodayProgress] = useState({ done: 0, total: 0 });
  const [v2BusinessName, setV2BusinessName] = useState('');
  // Round 101 — universal completion routing driven by the mission
  // metadata returned from `/api/todays-mission-v2`. `workflowTarget`
  // decides Generate vs Ask; `workflowContext` is the full mission
  // context blob the chip threads into the builder / Ask PayGBot.
  const [v2WorkflowTarget, setV2WorkflowTarget] = useState(null);
  const [v2WorkflowContext, setV2WorkflowContext] = useState(null);
  // Round 105 (2026-02-14) — customer draft edits captured inline
  // from the ExecutionPanel. `v2PendingEdits` holds ONLY the
  // fields the customer touched (delta over the seed payload),
  // so "Approve & Generate" can save them via /save-edits and
  // thread them into the strategist as approved edits.
  const [v2PendingEdits, setV2PendingEdits] = useState({});
  const [inlinePublishing, setInlinePublishing] = useState(false);

  useEffect(() => {
    fetchTodayContent();
    fetchTodayMissionCta();
    fetchTodayMissionV2();
    
    // Load saved completed tasks from localStorage
    const saved = localStorage.getItem('dailyMissionProgress');
    if (saved) {
      const parsed = JSON.parse(saved);
      const today = new Date().toDateString();
      if (parsed.date === today) {
        setCompletedTasks(parsed.tasks || {});
      }
    }

    // Round 96 — refresh Today's Mission + Business Health when
    // any bot page reports back that its deep-linked task was
    // just completed. This closes the TurboTax loop without polling.
    const handleTaskCompleted = () => {
      fetchTodayContent(true);
      fetchTodayMissionCta();
      fetchTodayMissionV2();
    };
    window.addEventListener('paygbot:task-completed', handleTaskCompleted);
    return () => {
      window.removeEventListener('paygbot:task-completed', handleTaskCompleted);
    };
  }, []);

  // Save completed tasks to localStorage and notify StreakProgressCard
  useEffect(() => {
    if (Object.keys(completedTasks).length > 0) {
      localStorage.setItem('dailyMissionProgress', JSON.stringify({
        date: new Date().toDateString(),
        tasks: completedTasks
      }));
      
      // Dispatch event for StreakProgressCard
      const completedCount = Object.values(completedTasks).filter(Boolean).length;
      window.dispatchEvent(new CustomEvent('dailyMissionUpdate', {
        detail: { completedCount, totalCount: 7 }
      }));
      
      // Also call global update function if available
      if (window.updateStreakProgress) {
        window.updateStreakProgress(completedCount, 7);
      }
    }
  }, [completedTasks]);

  const fetchTodayContent = async (silent = false) => {
    try {
      const response = await api.get('/today-content');
      setTodayData(response.data);
      
      if (response.data?.posting_streak) {
        setStreakData(response.data.posting_streak);
      }
      
      // Mark content tasks as complete if content exists
      if (response.data?.today?.content) {
        setCompletedTasks(prev => ({
          ...prev,
          create_content: true
        }));
      }
      
      // Mark post tasks complete if posted today
      if (response.data?.posting_streak?.posted_today) {
        setCompletedTasks(prev => ({
          ...prev,
          create_content: true,
          copy_prompt: true,
          post_content: true
        }));
      }
    } catch (error) {
      console.error('Failed to fetch today content:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchTodayMissionCta = async () => {
    try {
      const response = await contentAPI.getTodayMission();
      setMissionCta(response.data || null);
    } catch (error) {
      // Non-fatal — card will fall back to default CTA logic.
      console.error('Failed to fetch today mission CTA:', error);
    }
  };

  const fetchTodayMissionV2 = async () => {
    // Phase 2 — pull the asset-driven mission. If the user has at
    // least one ready asset in the registry, we'll surface its
    // execution label instead of the generic "Create today's
    // content" task. Failure is non-fatal; legacy task list is
    // shown.
    // Round 57 — also store the hydrated `asset_payload`, `tier`,
    // and `today_progress` so the new ExecutionPanel can render the
    // FULL asset inline (the primary execution surface).
    try {
      const response = await contentAPI.getTodayMissionV2();
      const data = response?.data || {};
      const m = data.mission;
      if (data.has_mission && m && m.asset_id) {
        setV2Mission({
          asset_id: m.asset_id,
          asset_type: m.asset_type,
          slot_index: m.slot_index,
          title: m.title,
          action: m.action,
          summary: m.summary,
          status: m.status,
          finalized_at: m.finalized_at || null,
        });
        setV2AssetPayload(data.asset_payload || null);
        setV2PendingEdits({});
        setV2Tier(data.tier || 'starter');
        setV2TierNote(data.tier_note || '');
        setV2TodayProgress(data.today_progress || { done: 0, total: 0 });
        setV2WorkflowTarget(m.workflow_target || null);
        setV2WorkflowContext(m.workflow_context || null);
      } else {
        setV2Mission(null);
        setV2AssetPayload(null);
        setV2TodayProgress(data.today_progress || { done: 0, total: 0 });
        setV2WorkflowTarget(null);
        setV2WorkflowContext(null);
      }
    } catch (error) {
      console.warn('todays-mission-v2 unavailable on DailyMissionCard:', error);
    }

    // Best-effort active-business name pill — non-fatal.
    try {
      const res = await api.get('/run-my-business-v2/summary');
      const bn = res?.data?.active_business?.business_name;
      if (bn) setV2BusinessName(bn);
    } catch {
      /* noop */
    }
  };

  // Round 57 — inline "Mark as posted/sent/pitched". Calls the v2
  // done endpoint, takes the inline-returned next mission + payload,
  // and advances the panel without a page reload.
  const markPublishedInline = async () => {
    if (!v2Mission?.asset_id) return;
    setInlinePublishing(true);
    try {
      const res = await api.post(`/todays-mission-v2/${v2Mission.asset_id}/done`);
      const data = res?.data || {};
      toast.success('Marked as published — nice work.');
      setCompletedTasks((prev) => ({ ...prev, create_content: true }));
      if (data.next_mission && data.next_asset_payload) {
        const nm = data.next_mission;
        setV2Mission({
          asset_id: nm.asset_id,
          asset_type: nm.asset_type,
          slot_index: nm.slot_index,
          title: nm.title,
          action: nm.action,
          summary: nm.summary,
          status: nm.status,
          finalized_at: nm.finalized_at || null,
        });
        setV2AssetPayload(data.next_asset_payload);
        setV2PendingEdits({});
        setV2Tier(data.tier || v2Tier);
        setV2TierNote(data.tier_note || '');
        setV2WorkflowTarget(nm.workflow_target || null);
        setV2WorkflowContext(nm.workflow_context || null);
      } else {
        setV2Mission(null);
        setV2AssetPayload(null);
        setV2WorkflowTarget(null);
        setV2WorkflowContext(null);
      }
      setV2TodayProgress(data.today_progress || v2TodayProgress);
    } catch (err) {
      console.error('Failed to mark published:', err);
      toast.error('Could not mark as published.');
    } finally {
      setInlinePublishing(false);
    }
  };

  // Phase 2 — asset-driven execution. Clicking an asset-backed task
  // opens the saved asset in a lightweight review modal (no new page,
  // no route, reuses the shadcn Dialog primitive). The customer
  // reviews and publishes — they do NOT regenerate.
  const [assetModal, setAssetModal] = useState(null); // { assetId, assetType, title, meta }
  const [assetLoading, setAssetLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);

  const openAssetForReview = async (task) => {
    const assetId = task.assetId;
    if (!assetId) return false;
    setAssetLoading(true);
    setAssetModal({ assetId, assetType: task.assetType, title: task.label });
    try {
      const res = await api.get(`/assets/${assetId}`);
      const doc = res?.data || {};
      setAssetModal({
        assetId,
        assetType: doc.asset_type || task.assetType,
        title: doc.title || task.label,
        summary: doc.summary || '',
        status: doc.status || 'ready',
        meta: doc.meta || {},
      });
    } catch (err) {
      console.error('Failed to load asset for review:', err);
      toast.error('Could not load this asset. Try again.');
      setAssetModal(null);
    } finally {
      setAssetLoading(false);
    }
    return true;
  };

  const publishAsset = async () => {
    if (!assetModal?.assetId) return;
    setPublishing(true);
    try {
      await api.post(`/todays-mission-v2/${assetModal.assetId}/done`);
      toast.success('Published — nice work.');
      // Locally mark complete + close + refresh next mission.
      setCompletedTasks((prev) => ({ ...prev, create_content: true }));
      setAssetModal(null);
      await fetchTodayMissionV2();
    } catch (err) {
      console.error('Failed to publish asset:', err);
      toast.error('Could not mark as published.');
    } finally {
      setPublishing(false);
    }
  };

  const copyText = async (text, label = 'Copied') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied to clipboard.`);
    } catch (err) {
      console.error('Clipboard failed:', err);
    }
  };

  const toggleTask = (taskId) => {
    setCompletedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  // Get tasks from daily_mission or build default
  const getDailyMission = () => {
    let mission;
    if (todayData?.daily_mission) {
      mission = todayData.daily_mission;
    } else {
      // Default structure if API doesn't return daily_mission
      const niche = todayData?.business_context?.niche || 'your niche';
      const hasContent = !!todayData?.today?.content;
      const posted = todayData?.posting_streak?.posted_today;

      mission = {
        title: "Today's Mission",
        subtitle: "Complete these tasks in minutes",
        tasks: [
          // Content Tasks
          { id: 'create_content', label: "Create today's content", completed: hasContent, category: 'content' },
          { id: 'copy_prompt', label: 'Copy your video prompt', completed: posted, category: 'content' },
          { id: 'post_content', label: 'Post to Instagram/TikTok', completed: posted, category: 'content' },
          // Engagement Tasks
          { id: 'comment_posts', label: `Comment on 5 posts in ${niche}`, completed: false, category: 'engagement' },
          { id: 'reply_comments', label: 'Reply to all comments on your content', completed: false, category: 'engagement' },
          // Growth Tasks
          { id: 'follow_accounts', label: 'Follow 10 relevant accounts', completed: false, category: 'growth' },
          { id: 'send_dms', label: 'Send 3 DMs or replies', completed: false, category: 'growth' }
        ]
      };
    }

    // 2026-06-10 Phase 2 — when the asset registry has a usable
    // mission, override the first content task's label so the
    // customer sees execution language ("Post Content #N — …")
    // instead of "Create today's content". Layout / count /
    // completion tracking are preserved.
    if (v2Mission && Array.isArray(mission?.tasks)) {
      mission = {
        ...mission,
        tasks: mission.tasks.map((t) => {
          if (t?.id === 'create_content') {
            return {
              ...t,
              label: v2Mission.action || `Post: ${v2Mission.title}`,
              assetId: v2Mission.asset_id,
              assetType: v2Mission.asset_type,
            };
          }
          return t;
        }),
      };
    }
    return mission;
  };

  const getIconForTask = (taskId, category) => {
    const iconMap = {
      'create_content': PenNib,
      'copy_prompt': Copy,
      'post_content': Rocket,
      'comment_posts': Chat,
      'reply_comments': ChatCircleDots,
      'follow_accounts': UserPlus,
      'send_dms': Heart
    };
    return iconMap[taskId] || CheckCircle;
  };

  const getCategoryColor = (category) => {
    const colors = {
      content: { bg: 'bg-violet-500/20', border: 'border-violet-500/30', text: 'text-violet-400', icon: 'bg-violet-500/30' },
      engagement: { bg: 'bg-blue-500/20', border: 'border-blue-500/30', text: 'text-blue-400', icon: 'bg-blue-500/30' },
      growth: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: 'bg-emerald-500/30' }
    };
    return colors[category] || colors.content;
  };

  const getCategoryLabel = (category) => {
    const labels = {
      content: 'Content',
      engagement: 'Engagement',
      growth: 'Growth'
    };
    return labels[category] || category;
  };

  const getCategoryIcon = (category) => {
    const icons = {
      content: VideoCamera,
      engagement: Chat,
      growth: Users
    };
    return icons[category] || Lightning;
  };

  // Compact per-task action — Round 103 (2026-07-24).
  // Four action modes driven purely by mission metadata + the
  // current hydrated asset_payload. No per-task hard-coding of
  // routes / labels — everything below is derived.
  //
  //   GENERATE  — mission asset does not yet exist / is skeleton
  //               (chip deep-links into the correct builder)
  //   COPY      — the exact field the task references already
  //               exists on asset_payload (no bot, no regen)
  //   VIEW      — the mission asset is fully hydrated; the task
  //               is really "look at what's there" (scrolls to
  //               ExecutionPanel)
  //   ASK       — human execution / judgment / adaptation
  //               (opens Ask PayGBot with full context)
  //
  // Unknown / ambiguous tasks safely default to ASK.
  const getCompactTaskAction = (task) => {
    if (!task) return null;
    const isComplete = completedTasks[task.id] || task.completed;
    if (isComplete) return null;

    const encodeCtx = (obj) => {
      try { return btoa(unescape(encodeURIComponent(JSON.stringify(obj)))); }
      catch { return ''; }
    };
    const taskCtx = {
      task_id: task.id,
      task_label: task.label || '',
      task_description: task.description || task.instructions || '',
      task_category: task.category || '',
    };
    const wt = v2WorkflowTarget;
    const wc = v2WorkflowContext;
    const payload = v2AssetPayload || {};
    const missionHydrated = !!(
      payload.hook || payload.caption || payload.script || payload.voice_script
      || payload.body
    );

    // Field-copy map — task IDs that surface an existing field
    // from the hydrated asset. Extend here to add new copy tasks.
    const COPY_MAP = {
      copy_prompt:  { field: 'video_prompt', label: 'Copy Prompt' },
      copy_hook:    { field: 'hook',         label: 'Copy Hook' },
      copy_caption: { field: 'caption',      label: 'Copy Caption' },
      copy_cta:     { field: 'cta',          label: 'Copy CTA' },
    };
    if (COPY_MAP[task.id] && payload[COPY_MAP[task.id].field]) {
      const cm = COPY_MAP[task.id];
      return {
        kind: 'copy', variant: 'copy',
        label: cm.label, icon: ChatCircleDots,
        onClick: async () => {
          try {
            await navigator.clipboard.writeText(String(payload[cm.field]));
            toast.success(`${cm.label.replace('Copy ', '')} copied.`);
          } catch {
            toast.error('Copy failed — long-press the field instead.');
          }
        },
      };
    }

    // VIEW map — task IDs where the customer just needs to look
    // at the existing asset (no generation, no bot). Only fires
    // when the mission asset is genuinely hydrated.
    const VIEW_TASK_IDS = new Set(['view_asset', 'post_content_view']);
    const looksLikePostContent = /^post_content(?:_|$)/.test(task.id)
      || /^post\s+content/i.test(task.label || '');
    if (missionHydrated && (VIEW_TASK_IDS.has(task.id)
        || (looksLikePostContent && task.category === 'content'))) {
      // Only treat as VIEW when we have a scroll target.
      const canScroll = typeof document !== 'undefined'
        && document.querySelector('[data-testid="daily-mission-card"]');
      if (canScroll && missionHydrated && task.id !== 'post_content') {
        return {
          kind: 'view', variant: 'view',
          label: 'View Asset', icon: PenNib,
          onClick: () => {
            document.querySelector('[data-testid="daily-mission-card"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
        };
      }
    }

    // GENERATE — mission has a machine builder AND is a creation
    // step in the content category (task.id !== post_content).
    const missionCanGenerate = wt && wt.mode === 'generate' && wt.route;
    const isCreationTask =
      missionCanGenerate
      && task.category === 'content'
      && task.id !== 'post_content';
    if (isCreationTask) {
      // Round 105 — Today's Mission is an editable draft. The
      // presence of hook / caption / cta does NOT mean the
      // strategist has completed generation. Only surface the
      // "View" chip once ``mission.finalized_at`` is set (by
      // the /finalize endpoint after the strategist ran).
      const isFinalized = !!v2Mission?.finalized_at;
      if (isFinalized) {
        return {
          kind: 'view', variant: 'view',
          label: 'View Asset', icon: PenNib,
          onClick: () => {
            document.querySelector('[data-testid="daily-mission-card"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
        };
      }
      const ctx = {
        ...(wc || {}),
        ...taskCtx,
        approved_edits: { ...(payload || {}), ...(v2PendingEdits || {}) },
      };
      const qs = new URLSearchParams({
        auto_generate: '1',
        mode: 'create',
        return_to: '/dashboard',
        asset_type: wt.asset_focus || wc?.asset_type || 'post',
        asset_id: wc?.asset_id || '',
        mission_id: wc?.mission_id || wc?.asset_id || '',
        task_id: task.id,
        business_id: wc?.business_id || '',
        wctx: encodeCtx(ctx),
      });
      return {
        kind: 'generate',
        label: wt.chip_label || 'Generate',
        icon: PenNib,
        variant: wt.chip_variant || 'content',
        onClick: () => navigate(`${wt.route}?${qs.toString()}`),
      };
    }

    // ASK PayGBot — safe fallback (human execution / judgment /
    // adaptation / unknown tasks).
    const askCtx = { ...(wc || {}), ...taskCtx };
    const qs = new URLSearchParams({
      prompt: task.label || '',
      wctx: encodeCtx(askCtx),
    });
    return {
      kind: 'ask', variant: 'ask',
      label: 'Ask PayGBot', icon: ChatCircleDots,
      onClick: () => navigate(`/ask-paygbot?${qs.toString()}`),
    };
  };

  const dailyMission = getDailyMission();
  const tasks = dailyMission.tasks || [];
  
  // Group tasks by category
  const tasksByCategory = tasks.reduce((acc, task) => {
    const category = task.category || 'content';
    if (!acc[category]) acc[category] = [];
    acc[category].push(task);
    return acc;
  }, {});

  // Calculate progress
  const totalTasks = tasks.length;
  const completedCount = tasks.filter(t => completedTasks[t.id] || t.completed).length;
  const progress = totalTasks > 0 ? (completedCount / totalTasks) * 100 : 0;
  const allComplete = completedCount === totalTasks;

  // Get button config
  const getButtonConfig = () => {
    // Round 103 (2026-07-24) — v2 mission takes priority over
    // any legacy `missionCta.workflow_target` / `cta_route` from
    // /api/content/today-mission. When /api/todays-mission-v2
    // returns a hydrated mission the orange CTA MUST continue
    // that exact mission (View if fully generated, Generate if
    // skeleton). Legacy branches only fire when v2 has nothing.
    if (v2Mission && v2WorkflowTarget) {
      const payload = v2AssetPayload || {};
      const wc = v2WorkflowContext || {};
      const wt = v2WorkflowTarget;
      // Round 105 (2026-02-14) — Today's Mission is an EDITABLE
      // DRAFT. The presence of hook / caption / cta in the
      // payload does NOT mean the strategist has completed
      // generation — those fields are seeded from the Business
      // Brain and the customer is expected to review + edit them.
      // The button flips to "View Generated Asset" ONLY when the
      // strategist actually finalizes (finalized_at is stamped
      // by POST /todays-mission-v2/{asset_id}/finalize).
      const isFinalized = !!v2Mission.finalized_at;
      if (isFinalized) {
        return {
          action: () => {
            document.querySelector('[data-testid="daily-mission-card"]')
              ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          },
          label: 'View Generated Asset',
          icon: CheckCircle,
        };
      }
      if (wt.mode === 'generate' && wt.route) {
        return {
          action: async () => {
            try {
              // Round 105 — "Approve & Generate". The customer's
              // inline edits from the ExecutionPanel (v2PendingEdits)
              // + the currently displayed payload are the approved
              // source of truth. Merge them, persist to Mongo via
              // /save-edits (idempotent, no duplicate), THEN route
              // to the strategist with the exact approved values
              // in wctx.approved_edits.
              const approvedEdits = {
                ...(v2AssetPayload || {}),
                ...(v2PendingEdits || {}),
              };
              try {
                await api.post(
                  `/todays-mission-v2/${v2Mission.asset_id}/save-edits`,
                  { edits: v2PendingEdits || {} },
                );
              } catch (saveErr) {
                console.warn('[Approve & Generate] save-edits failed (continuing):', saveErr);
              }
              const ctx = {
                ...wc,
                task_id: 'orange_cta',
                task_label: 'Approve & Generate',
                approved_edits: approvedEdits,
              };
              const enc = btoa(unescape(encodeURIComponent(JSON.stringify(ctx))));
              const qs = new URLSearchParams({
                auto_generate: '1',
                mode: 'create',
                return_to: '/dashboard',
                asset_type: wt.asset_focus || wc.asset_type || 'post',
                asset_id: wc.asset_id || '',
                mission_id: wc.mission_id || wc.asset_id || '',
                task_id: 'orange_cta',
                business_id: wc.business_id || '',
                wctx: enc,
              });
              navigate(`${wt.route}?${qs.toString()}`);
            } catch {
              onCreateVideos && onCreateVideos();
            }
          },
          label: 'Approve & Generate',
          icon: PenNib,
        };
      }
    }

    // Round 95 — Prefer workflow_target when the backend feature
    // flag is on. `<TaskActionButton />` handles the deep-link
    // encoding (asset_id, task_id, return_to, mode) so bot pages
    // can hydrate their edit modal directly. When workflow_target
    // is absent (flag off, or unresolvable topic), we fall back
    // to the legacy cta_label / cta_route behaviour below —
    // pre-Round-94 pages remain unchanged.
    if (missionCta?.workflow_target?.route) {
      const wt = missionCta.workflow_target;
      const qs = new URLSearchParams();
      if (wt.asset_id) qs.set('asset_id', wt.asset_id);
      if (wt.task_id) qs.set('task_id', wt.task_id);
      if (wt.return_to) qs.set('return_to', wt.return_to);
      if (wt.mode) qs.set('mode', wt.mode);
      if (wt.asset_type) qs.set('asset_type', wt.asset_type);
      const url = qs.toString() ? `${wt.route}?${qs.toString()}` : wt.route;
      return {
        action: () => navigate(url),
        label: missionCta.cta_label || wt.intent || 'Open',
        icon: Rocket,
        useRouteTestId: true,
        // Test-visible metadata for Round 95 acceptance tests.
        workflowTaskId: wt.task_id,
        workflowMode: wt.mode,
        workflowResolution: wt.resolution_reason,
      };
    }

    // 2026-05-29 Phase 2 — Backend-driven CTA route wins over the
    // default Plan-Week / Generate-Content / Mission-Complete flow.
    // The backend's workflow_router (formerly mission_cta_router)
    // maps the mission topic to the correct bot surface (Content
    // Bot, Money Bot, Weekly Planner, etc.). When both label and
    // route are present, route the user there with react-router
    // instead of opening a local modal.
    if (missionCta?.cta_label && missionCta?.cta_route) {
      return {
        action: () => navigate(missionCta.cta_route),
        label: missionCta.cta_label,
        icon: Rocket,
        useRouteTestId: true,
      };
    }

    const hasWeeklyPlan = todayData?.has_weekly_plan;
    const hasContent = !!todayData?.today?.content;
    
    if (!hasWeeklyPlan) {
      return { 
        action: onPlanWeek, 
        label: 'Run My Week', 
        icon: Calendar 
      };
    }

    if (!hasContent) {
      return { 
        action: onCreateVideos, 
        label: "Generate My Content →", 
        icon: PenNib 
      };
    }
    
    if (allComplete) {
      return { 
        action: () => toast.success('Mission complete! Great work today!'), 
        label: 'Mission Complete!', 
        icon: CheckCircle 
      };
    }
    
    return { 
      action: () => toast.info('Complete all tasks to finish today\'s mission!'), 
      label: 'Keep Going!', 
      icon: Rocket 
    };
  };

  const buttonConfig = getButtonConfig();

  if (loading) {
    return (
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-white/10 rounded w-1/3" />
          <div className="h-20 bg-white/10 rounded" />
          <div className="h-10 bg-white/10 rounded w-1/2" />
        </div>
      </div>
    );
  }

  // No weekly plan state
  if (!todayData?.has_weekly_plan) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-2xl p-6"
        data-testid="daily-mission-card"
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
            <Calendar size={32} className="text-white" weight="fill" />
          </div>
          <h3 className="text-xl font-bold text-white mb-2">Ready to plan your week?</h3>
          <p className="text-slate-400 mb-6 max-w-sm mx-auto">
            Get your daily missions and know exactly what to do each day.
          </p>
          <button
            onClick={onPlanWeek}
            data-testid="run-my-week-btn"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-xl transition-all"
          >
            <Lightning size={20} weight="fill" />
            <span>Run My Week</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 border border-white/10 rounded-2xl overflow-hidden"
      data-testid="daily-mission-card"
    >
      {/* Streak Banner */}
      {streakData?.current_streak > 0 && (
        <div className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border-b border-orange-500/30 flex items-center justify-center space-x-2">
          <Fire size={16} weight="fill" className="text-orange-400" />
          <span className="text-orange-400 font-semibold text-sm">
            {streakData.current_streak} Day Streak
          </span>
          {streakData.posted_today && (
            <>
              <span className="text-orange-400/50">•</span>
              <span className="text-green-400 text-xs flex items-center space-x-1">
                <CheckCircle size={12} weight="fill" />
                <span>Posted today</span>
              </span>
            </>
          )}
        </div>
      )}
      
      {/* Header */}
      <div className="px-4 sm:px-6 py-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
              <Lightning size={20} className="text-white" weight="fill" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Today's Mission</h2>
              <p className="text-amber-400/80 text-sm">Complete in minutes</p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="text-right">
            <span className="text-amber-400 text-sm font-bold">{completedCount}/{totalTasks}</span>
            <div className="w-16 h-1.5 bg-white/10 rounded-full mt-1 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Round 57 — Execution Panel: the FULL asset rendered inline
          so the user can copy + ship without opening any other bot.
          Only shown when /api/todays-mission-v2 returned a hydrated
          asset_payload. Falls back to the legacy task list below
          when no v2 mission is available. */}
      {v2Mission && v2AssetPayload && (
        <div className="px-4 sm:px-6 pt-4">
          <ExecutionPanel
            tier={v2Tier}
            tierNote={v2TierNote}
            mission={v2Mission}
            assetPayload={v2AssetPayload}
            todayProgress={v2TodayProgress}
            businessName={v2BusinessName}
            publishing={inlinePublishing}
            onMarkPublished={markPublishedInline}
            editable={!v2Mission.finalized_at}
            pendingEdits={v2PendingEdits}
            onEditsChange={setV2PendingEdits}
          />
        </div>
      )}

      {/* Tasks by Category */}
      <div className="p-4 sm:p-5 space-y-4">
        {['content', 'engagement', 'growth'].map(category => {
          const categoryTasks = tasksByCategory[category] || [];
          if (categoryTasks.length === 0) return null;
          
          const colors = getCategoryColor(category);
          const CategoryIcon = getCategoryIcon(category);
          const categoryCompleted = categoryTasks.filter(t => completedTasks[t.id] || t.completed).length;
          
          return (
            <div key={category} className="space-y-2">
              {/* Category Header */}
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center space-x-2">
                  <CategoryIcon size={14} className={colors.text} weight="fill" />
                  <span className={`text-xs font-semibold uppercase tracking-wider ${colors.text}`}>
                    {getCategoryLabel(category)}
                  </span>
                </div>
                <span className="text-xs text-slate-500">
                  {categoryCompleted}/{categoryTasks.length}
                </span>
              </div>
              
              {/* Tasks */}
              <div className="space-y-1.5">
                {categoryTasks.map((task, index) => {
                  const Icon = getIconForTask(task.id, category);
                  const isComplete = completedTasks[task.id] || task.completed;
                  const compactAction = getCompactTaskAction(task);
                  
                  return (
                    <motion.div
                      key={task.id}
                      role="button"
                      tabIndex={0}
                      onClick={async () => {
                        // Phase 2 — asset-driven execution. If this
                        // task is backed by a registered asset, open
                        // it for review/publish instead of toggling
                        // completion (which would do nothing useful
                        // until the customer actually shipped).
                        if (task.assetId) {
                          const opened = await openAssetForReview(task);
                          if (opened) return;
                        }
                        toggleTask(task.id);
                      }}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className={`w-full flex items-center justify-between p-3 rounded-lg transition-all cursor-pointer ${
                        isComplete 
                          ? 'bg-green-500/10 border border-green-500/20' 
                          : `${colors.bg} border ${colors.border} hover:bg-white/5`
                      }`}
                      data-testid={`mission-task-${task.id}`}
                    >
                      <div className="flex items-center space-x-3 min-w-0">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
                          isComplete ? 'bg-green-500/30' : colors.icon
                        }`}>
                          {isComplete ? (
                            <CheckCircle size={16} className="text-green-400" weight="fill" />
                          ) : (
                            <Icon size={16} className={colors.text} />
                          )}
                        </div>
                        <span className={`text-sm font-medium truncate ${
                          isComplete ? 'text-green-400 line-through' : 'text-white'
                        }`}>
                          {task.label}
                        </span>
                      </div>

                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {/* Compact per-task action chip (post-rollback
                            single dashboard enhancement). Generate for
                            asset-generating tasks; Ask PayGBot for
                            human-action tasks. Hidden once complete. */}
                        {compactAction && (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); compactAction.onClick(); }}
                            data-testid={`mission-task-${task.id}-${compactAction.kind}`}
                            data-workflow-variant={compactAction.variant || compactAction.kind}
                            className={`inline-flex items-center space-x-1 px-2 py-1 rounded-md text-[10px] font-semibold transition-all ${
                              compactAction.kind === 'generate'
                                ? 'bg-violet-500/30 hover:bg-violet-500/40 text-violet-100 border border-violet-400/40'
                                : 'bg-white/10 hover:bg-white/20 text-slate-200 border border-white/20'
                            }`}
                          >
                            <compactAction.icon size={11} weight="bold" />
                            <span>{compactAction.label}</span>
                          </button>
                        )}

                        {/* Checkmark indicator */}
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isComplete 
                            ? 'bg-green-500 border-green-500' 
                            : 'border-white/20'
                        }`}>
                          {isComplete && <CheckCircle size={12} className="text-white" weight="bold" />}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* CTA */}
      <div className="px-4 sm:px-5 pb-4 sm:pb-5">
        <button
          onClick={buttonConfig.action}
          data-testid={buttonConfig.useRouteTestId ? 'mission-cta-route-btn' : 'mission-cta-btn'}
          data-workflow-task-id={buttonConfig.workflowTaskId || ''}
          data-workflow-mode={buttonConfig.workflowMode || ''}
          data-workflow-resolution={buttonConfig.workflowResolution || ''}
          className={`w-full flex items-center justify-center space-x-2 py-3 rounded-xl font-semibold transition-all ${
            allComplete
              ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white'
              : 'bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white'
          }`}
        >
          {buttonConfig.icon && <buttonConfig.icon size={18} weight="fill" />}
          <span>{buttonConfig.label}</span>
          {!allComplete && <ArrowRight size={16} />}
        </button>

        {/* Post-Round-96 execution loop — for tasks that require
             human action (commenting, replying, phone calls, etc.)
             show a small "Need help? Ask PayGBot" pill under the
             main CTA so the customer is never left wondering what
             to do. Only rendered when execution_loop_v1 is on AND
             the resolved workflow_target flags this task as
             human_action. */}
        {flags.execution_loop_v1 && missionCta?.workflow_target
          && missionCta.workflow_target.execution_kind === 'human_action' && (
            <div className="mt-3 flex justify-center">
              <AskPayGBotHelperButton
                task={missionCta}
                testId="mission-ask-paygbot-helper"
              />
            </div>
          )}
        
        {/* Encouragement text */}
        {!allComplete && completedCount > 0 && (
          <p className="text-center text-slate-500 text-xs mt-2">
            {totalTasks - completedCount} tasks left - you've got this!
          </p>
        )}
      </div>

      {/* Phase 2 — Asset review/publish modal. Opens when the user
          clicks an asset-backed task. Shows the saved caption,
          hashtags, CTA, and video prompt straight from the
          registry. The customer reviews and publishes — they do
          NOT regenerate. */}
      <Dialog open={!!assetModal} onOpenChange={(o) => { if (!o) setAssetModal(null); }}>
        <DialogContent className="max-w-2xl bg-slate-900 text-white border border-slate-700" data-testid="asset-review-modal">
          <DialogHeader>
            <DialogTitle className="text-white">
              {assetModal?.title || 'Your saved asset'}
            </DialogTitle>
            {assetModal?.assetType && (
              <p className="text-xs uppercase tracking-wider text-slate-400">
                {String(assetModal.assetType).replace(/_/g, ' ')}
                {assetModal?.status ? ` · ${assetModal.status}` : ''}
              </p>
            )}
          </DialogHeader>

          {assetLoading ? (
            <div className="py-10 text-center text-slate-400">Loading…</div>
          ) : (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {assetModal?.meta?.hook && (
                <AssetField label="Hook" value={assetModal.meta.hook} onCopy={copyText} />
              )}
              {(assetModal?.meta?.caption || assetModal?.summary) && (
                <AssetField
                  label="Caption"
                  value={assetModal?.meta?.caption || assetModal?.summary}
                  multi
                  onCopy={copyText}
                />
              )}
              {assetModal?.meta?.body && assetModal.meta.body !== assetModal?.meta?.caption && (
                <AssetField label="Full text" value={assetModal.meta.body} multi onCopy={copyText} />
              )}
              {Array.isArray(assetModal?.meta?.hashtags) && assetModal.meta.hashtags.length > 0 && (
                <AssetField label="Hashtags" value={assetModal.meta.hashtags.join(' ')} onCopy={copyText} />
              )}
              {assetModal?.meta?.cta && (
                <AssetField label="Call to action" value={assetModal.meta.cta} onCopy={copyText} />
              )}
              {assetModal?.meta?.video_prompt && (
                <AssetField label="Video prompt" value={assetModal.meta.video_prompt} multi onCopy={copyText} />
              )}
              {!assetModal?.meta && !assetModal?.summary && (
                <p className="text-slate-400 text-sm">This asset has no preview content saved.</p>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <button
              onClick={() => setAssetModal(null)}
              className="px-4 py-2 text-sm text-slate-300 hover:text-white"
              data-testid="asset-review-close"
            >
              Close
            </button>
            <button
              onClick={publishAsset}
              disabled={publishing || assetLoading || !assetModal?.assetId}
              data-testid="asset-mark-published"
              className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white disabled:opacity-50"
            >
              {publishing ? 'Marking…' : 'Mark as Published'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

const AssetField = ({ label, value, multi, onCopy }) => (
  <div className="rounded-lg border border-slate-700 bg-slate-800/50 p-3">
    <div className="flex items-center justify-between mb-1">
      <p className="text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <button
        onClick={() => onCopy?.(value, label)}
        className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1"
        data-testid={`asset-copy-${String(label).toLowerCase().replace(/\s+/g, '-')}`}
      >
        <Copy size={12} /> Copy
      </button>
    </div>
    {multi ? (
      <p className="text-sm text-white whitespace-pre-wrap">{value}</p>
    ) : (
      <p className="text-sm text-white">{value}</p>
    )}
  </div>
);

export default DailyMissionCard;
